import { apiSlice } from '@/app/api/apiSlice';

export const buyerApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // Dashboard
    getBuyerDashboard: builder.query({
      query: () => '/users/dashboard/',
      providesTags: ['BuyerDashboard', 'Enterprise'],
    }),

    // Animator Endpoints
    getMyEnterpriseAnimators: builder.query({
      query: () => '/gestion/animators/',
      providesTags: (result = [], error, arg) => [
        'Animator',
        ...result.map(({ id }) => ({ type: 'Animator', id }))
      ],
    }),
    createAnimator: builder.mutation({
      query: animatorData => ({
        url: '/gestion/animators/',
        method: 'POST',
        body: animatorData,
      }),
      transformResponse: (response) => response,
      invalidatesTags: ['Animator'],
    }),
    updateAnimator: builder.mutation({
      query: ({ id, data }) => ({
        url: `/gestion/animators/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Animator', id: arg.id }],
    }),
    deleteAnimator: builder.mutation({
      query: ({ id }) => ({
        url: `/gestion/animators/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Animator', id: arg.id }],
    }),
    deleteManyAnimators: builder.mutation({
      query: ({ ids }) => ({
        url: '/gestion/animators/delete-many/',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['Animator'],
    }),

    // Member Endpoints
    getMyEnterpriseMembers: builder.query({
      query: () => '/gestion/members/',
      providesTags: (result = [], error, arg) => [
        'Member',
        ...result.map(({ id }) => ({ type: 'Member', id }))
      ],
    }),
    createMember: builder.mutation({
      query: memberData => ({
        url: '/gestion/members/',
        method: 'POST',
        body: memberData,
      }),
      invalidatesTags: ['Member'],
    }),
    updateMember: builder.mutation({
      query: ({ id, data }) => ({
        url: `/gestion/members/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Member', id: arg.id }],
    }),
    deleteMember: builder.mutation({
      query: ({ id }) => ({
        url: `/gestion/members/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Member', id: arg.id }],
    }),
    deleteManyMembers: builder.mutation({
      query: ({ ids }) => ({
        url: '/gestion/members/delete-many/',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['Member'],
    }),

    // Game Run Endpoints
    getGameRuns: builder.query({
      query: () => '/game/runs/',
      providesTags: (result = [], error, arg) => [
        'GameRun',
        ...result.map(({ id }) => ({ type: 'GameRun', id }))
      ],
    }),
    createGameRun: builder.mutation({
      query: gameRunData => ({
        url: '/gestion/gameruns/',
        method: 'POST',
        body: gameRunData,
      }),
      invalidatesTags: ['GameRun'],
    }),
    updateGameRun: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/gestion/gameruns/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameRun', id: arg.id }],
    }),
    terminateGameRun: builder.mutation({
      query: ({ id }) => ({
        url: `/gestion/gameruns/${id}/terminate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameRun', id: arg.id }],
    }),
    terminateManyGameRuns: builder.mutation({
      query: ({ ids }) => ({
        url: '/gestion/gameruns/terminate-many/',
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['GameRun'],
    }),

    // Group Endpoints
    createGroupsManually: builder.mutation({
      query: groupData => ({
        url: `/gestion/gameruns/${groupData.gameRunId}/create-groups-manually/`,
        method: 'POST',
        body: groupData,
      }),
      invalidatesTags: ['GameRun'], // Invalidate GameRuns to refetch group data
    }),
    getGameGroups: builder.query({
      query: (gameRunId) => `/gestion/gamegroups/?gameRun=${gameRunId}`,
      providesTags: (result = [], error, arg) => [
        'GameGroup',
        ...result.map(({ id }) => ({ type: 'GameGroup', id }))
      ],
    }),
    getGameGroupDetails: builder.query({
      query: (groupId) => `/gestion/gamegroups/${groupId}/`,
      providesTags: (result, error, arg) => [{ type: 'GameGroup', id: arg }],
    }),
    updateGameGroup: builder.mutation({
      query: ({ id, data }) => ({
        url: `/gestion/gamegroups/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameGroup', id: arg.id }],
    }),
    deleteGameGroup: builder.mutation({
      query: ({ id }) => ({
        url: `/gestion/gamegroups/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'GameGroup', id: arg.id }],
    }),

    // Results & Analysis Endpoints
    getBuyerGameRunAnswers: builder.query({
      query: (gameRunId) => `/gestion/gameruns/${gameRunId}/game-answers/`,
      providesTags: (result, error, gameRunId) => [
        { type: 'BuyerGameAnswer', id: gameRunId }
      ],
      transformResponse: (responseData) => {
        return responseData.grouped_answers || {};
      }
    }),
    getStrongGameExamples: builder.query({
      query: () => '/gestion/gameruns/strong-examples/',
      providesTags: ['StrongExamples']
    }),
    performEnterpriseAIAnalysis: builder.mutation({
      query: ({ gameRunId, groupIds }) => ({
        url: `/gestion/gameruns/${gameRunId}/enterprise-ai-analysis/`,
        method: 'POST',
        body: { group_ids: groupIds }
      })
    }),
  }),
});

export const {
  useGetBuyerDashboardQuery,
  useGetMyEnterpriseAnimatorsQuery,
  useCreateAnimatorMutation,
  useUpdateAnimatorMutation,
  useDeleteAnimatorMutation,
  useDeleteManyAnimatorsMutation,
  useGetMyEnterpriseMembersQuery,
  useCreateMemberMutation,
  useUpdateMemberMutation,
  useDeleteMemberMutation,
  useDeleteManyMembersMutation,
  useGetGameRunsQuery,
  useCreateGameRunMutation,
  useUpdateGameRunMutation,
  useTerminateGameRunMutation,
  useTerminateManyGameRunsMutation,
  useCreateGroupsManuallyMutation,
  useGetGameGroupsQuery,
  useGetGameGroupDetailsQuery,
  useUpdateGameGroupMutation,
  useDeleteGameGroupMutation,
  useGetBuyerGameRunAnswersQuery,
  useGetStrongGameExamplesQuery,
  usePerformEnterpriseAIAnalysisMutation,
} = buyerApiSlice;
